import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Content from '~/components/elements/Content'
import type { Page_Flexcontent_Flex_Text } from '~/graphql/types'
import { LosseLink } from '@ubo/losse-sjedel'

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export default function TextTypeform({ fields }: { fields: Page_Flexcontent_Flex_Text }) {
  const isFullPage = Boolean(fields?.fullPage)
  const fullPageTypeformRef = useRef<HTMLDivElement>(null)
  const [isFullPageReady, setIsFullPageReady] = useState(!isFullPage)

  useEffect(() => {
    if (!fields?.typeform) {
      return
    }

    const existingScript = document.querySelector('script[src="//embed.typeform.com/next/embed.js"]')

    if (!existingScript) {
      const script = document.createElement('script')
      script.src = '//embed.typeform.com/next/embed.js'
      document.body.appendChild(script)
    }
  }, [fields?.typeform])

  useIsomorphicLayoutEffect(() => {
    if (!isFullPage) {
      return
    }

    document.body.classList.add('typeform-fullpage')

    return () => {
      document.body.classList.remove('typeform-fullpage')
    }
  }, [isFullPage])

  useEffect(() => {
    if (!isFullPage || !fields?.typeform) {
      setIsFullPageReady(true)
      return
    }

    setIsFullPageReady(false)

    const hasEmbeddedIframe = () => Boolean(fullPageTypeformRef.current?.querySelector('iframe'))

    if (hasEmbeddedIframe()) {
      setIsFullPageReady(true)
      return
    }

    const observer = new MutationObserver(() => {
      if (hasEmbeddedIframe()) {
        setIsFullPageReady(true)
        observer.disconnect()
      }
    })

    if (fullPageTypeformRef.current) {
      observer.observe(fullPageTypeformRef.current, {
        childList: true,
        subtree: true
      })
    }

    return () => {
      observer.disconnect()
    }
  }, [fields?.typeform, isFullPage])

  return (
    <section
      id={fields.sectionid || 'TextTypeform'}
      className={isFullPage ? 'relative overflow-hidden pb-0' : 'relative bg-metric-hariyama overflow-hidden mb-10'}
    >
      {isFullPage ? (
        <div className="relative w-full h-screen mx-auto">
          {!isFullPageReady && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-metric-cinderace">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-metric-electrike border-t-transparent" />
            </div>
          )}
          <div ref={fullPageTypeformRef} data-tf-live={fields.typeform || ''} className="w-full h-screen mx-auto" />
        </div>
      ) : (
        <div className="container-right py-14 sm:py-20 relative">
          <div className="grid gap-10 lg:gap-20 relative z-10">
            <div className="flex items-center max-w-screen-md">
              <div>
                <Content className="content text-white children-h2:mb-6 children-h2:text-4xl children-h2:uppercase children-h2:font-black">
                  {fields.description}
                </Content>
                {fields.link && (
                  <LosseLink className="btn btn-metric-electrike mt-6" to={fields.link?.url || '/'} target={fields.link?.target || undefined}>
                    {fields.link?.title}
                  </LosseLink>
                )}
              </div>
            </div>
            {fields?.typeform && <div data-tf-live={fields.typeform} className="w-full h-[875px] mx-auto" />}
          </div>
        </div>
      )}
    </section>
  )
}
